import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, XCircle, Globe, MousePointer2, CheckCircle2, ArrowLeft } from 'lucide-react';

const THEME_COLOR = '#c59253';

const OrderingConfig = ({ state, updateState, onNext, onBack }) => {
    // view: 'main' (Yes/No), 'scope' (All/Specific), 'selection' (Space List)
    const [view, setView] = useState('main');

    const config = state.orderingConfig || { enabled: true, mode: 'all', enabledSpaces: [] };
    const spaces = state.spaces || [];

    const handleToggleOrdering = (enabled) => {
        const newConfig = { ...config, enabled };
        updateState({ orderingConfig: newConfig });
        if (enabled) {
            setView('scope');
        } else {
            // If disabled, we can technically proceed or just stay here. 
            // User requested flourished UI, so let's allow them to continue.
        }
    };

    const handleSelectMode = (mode) => {
        const newConfig = { ...config, mode };
        updateState({ orderingConfig: newConfig });
        if (mode === 'specific') {
            setView('selection');
        } else {
            // Mode is 'all', we are done with this step's specific settings
        }
    };

    const toggleSpace = (id) => {
        const selected = config.enabledSpaces || [];
        const newEnabled = selected.includes(id)
            ? selected.filter(sid => sid !== id)
            : [...selected, id];
        updateState({ orderingConfig: { ...config, enabledSpaces: newEnabled } });
    };

    const SelectionCard = ({ icon: Icon, title, description, active, onClick, color = THEME_COLOR }) => (
        <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            className={`card border-2 cursor-pointer transition-all mb-3 overflow-hidden ${active ? 'shadow-lg' : 'shadow-sm'}`}
            style={{
                borderColor: active ? color : '#f0f0f0',
                backgroundColor: active ? `${color}05` : '#fff',
                borderRadius: '1.25rem'
            }}
        >
            <div className="card-body p-4 d-flex align-items-center gap-4">
                <div
                    className="rounded-4 p-3 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: active ? `${color}15` : '#f8f9fa', color: active ? color : '#6c757d' }}
                >
                    <Icon size={32} />
                </div>
                <div className="flex-grow-1">
                    <h5 className={`fw-bold mb-1 ${active ? 'text-dark' : 'text-muted'}`}>{title}</h5>
                    <p className="text-muted small mb-0">{description}</p>
                </div>
                {active && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                        <CheckCircle2 size={24} color={color} />
                    </motion.div>
                )}
            </div>
        </motion.div>
    );

    return (
        <div className="h-100 overflow-auto bg-white">
            <div className="container py-5" style={{ maxWidth: '700px' }}>
                <AnimatePresence mode="wait">
                    {view === 'main' && (
                        <motion.div
                            key="main"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            <div className="text-center mb-5">
                                <h1 className="h2 fw-bold mb-2">Enable Ordering?</h1>
                                <p className="text-muted">Do you want customers to be able to place orders from their phones?</p>
                            </div>

                            <SelectionCard
                                icon={ShoppingBag}
                                title="Yes, Enable Ordering"
                                description="Customers can browse and order directly from the digital menu."
                                active={config.enabled === true}
                                onClick={() => handleToggleOrdering(true)}
                            />

                            <SelectionCard
                                icon={XCircle}
                                title="No, View Only"
                                description="The menu will be digital, but customers must order through a waiter."
                                active={config.enabled === false}
                                onClick={() => handleToggleOrdering(false)}
                                color="#dc3545"
                            />
                        </motion.div>
                    )}

                    {view === 'scope' && (
                        <motion.div
                            key="scope"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            <button onClick={() => setView('main')} className="btn btn-link p-0 mb-4 text-muted text-decoration-none d-flex align-items-center gap-2">
                                <ArrowLeft size={16} /> Back to previous
                            </button>

                            <div className="text-center mb-5">
                                <h1 className="h2 fw-bold mb-2">Ordering Scope</h1>
                                <p className="text-muted">Where should customers be allowed to place orders?</p>
                            </div>

                            <SelectionCard
                                icon={Globe}
                                title="All Spaces"
                                description="Ordering will be available globally across all your locations and tables."
                                active={config.mode === 'all'}
                                onClick={() => handleSelectMode('all')}
                            />

                            <SelectionCard
                                icon={MousePointer2}
                                title="Specific Spaces"
                                description="You choose exactly which areas or tables have ordering enabled."
                                active={config.mode === 'specific'}
                                onClick={() => handleSelectMode('specific')}
                            />
                        </motion.div>
                    )}

                    {view === 'selection' && (
                        <motion.div
                            key="selection"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            <button onClick={() => setView('scope')} className="btn btn-link p-0 mb-4 text-muted text-decoration-none d-flex align-items-center gap-2">
                                <ArrowLeft size={16} /> Back to scope
                            </button>

                            <div className="mb-4">
                                <h1 className="h2 fw-bold mb-2">Select Spaces</h1>
                                <p className="text-muted">Choose the spaces where ordering is allowed.</p>
                            </div>

                            <div className="d-flex flex-column gap-2 mb-4">
                                {spaces.filter(s => !s.parentId).map(space => {
                                    const isSelected = config.enabledSpaces?.includes(space.id);
                                    return (
                                        <motion.div
                                            key={space.id}
                                            whileTap={{ scale: 0.99 }}
                                            onClick={() => toggleSpace(space.id)}
                                            className="p-3 border rounded-3 d-flex align-items-center gap-3 cursor-pointer transition-all"
                                            style={{
                                                backgroundColor: isSelected ? `${THEME_COLOR}10` : 'transparent',
                                                borderColor: isSelected ? THEME_COLOR : '#eee'
                                            }}
                                        >
                                            <div
                                                className={`rounded-circle border-2 d-flex align-items-center justify-content-center`}
                                                style={{ width: '22px', height: '22px', borderColor: isSelected ? THEME_COLOR : '#ccc', backgroundColor: isSelected ? THEME_COLOR : 'transparent' }}
                                            >
                                                {isSelected && <CheckCircle2 size={14} color="white" />}
                                            </div>
                                            <div className="flex-grow-1">
                                                <span className={`fw-medium d-block ${isSelected ? 'text-dark' : 'text-muted'}`}>{space.name}</span>

                                            </div>
                                        </motion.div>
                                    );
                                })}
                                {spaces.length === 0 && (
                                    <div className="text-center py-4 bg-light rounded-3">
                                        <p className="text-muted mb-0">No spaces created yet. Ordering will be globally disabled.</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default OrderingConfig;
